import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    IconButton,
    Chip,
    CircularProgress,
    Tooltip,
    Button,
    SvgIcon
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CachedIcon from '@mui/icons-material/Cached';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import { useSelector } from 'react-redux';
import { formatCurrency, useCurrency } from '../../../utils/currency';
import { isUaeProperty } from '../../../utils/dubaiHospitalityTax';
import { FinanceService } from '../../../api/services/financeService';

import RecordCashDropModal from './RecordCashDropModal';

const MoneyBagIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10 3h4a1 1 0 0 1 1 1v1.5a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 5.5V4a1 1 0 0 1 1-1Z"
        />
        <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 7.5C4 11 3.5 17 6.5 20A3 3 0 0 0 8.5 21h7a3 3 0 0 0 2-1c3-3 2.5-9 .5-12.5H6Z"
        />
        <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 11v6"
        />
        <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.2 12.8h2.3a1.2 1.2 0 0 1 0 2.4H11a1.2 1.2 0 0 0 0 2.4h2.5"
        />
    </SvgIcon>
);

const DropCashIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14 4.5A9 9 0 1 0 19.5 10"
        />
        <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 4h5v5M20 4l-6.5 6.5"
        />
        <path
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8.5v7M10.3 10.2h2.4a1.2 1.2 0 0 1 0 2.4h-1.4a1.2 1.2 0 0 0 0 2.4h2.4"
        />
    </SvgIcon>
);

const OpeningFloatIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <rect x="2" y="5" width="20" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="10.5" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="M5 8v-.5M19 8v-.5M5 13v.5M19 13v.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="2" y1="19.5" x2="22" y2="19.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </SvgIcon>
);

const CashDrawerIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path d="M11 2h2v2h-2z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v6H6V7Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <rect x="8" y="7.5" width="3" height="2" rx="0.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <rect x="3" y="13" width="18" height="6.5" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <line x1="10" y1="16.5" x2="14" y2="16.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </SvgIcon>
);

const TotalInflowIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path
            d="M13 4H7a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <path
            d="M19 4l-5.5 5.5M13.5 5.5v4h4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M11 9.5v5M9.8 10.7h2.2a1 1 0 0 1 0 1.9H11a1 1 0 0 0 0 1.9h2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
        />
    </SvgIcon>
);

const TotalOutflowIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="9.5" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="M5.5 6.5v-.5M18.5 6.5v-.5M5.5 12v.5M18.5 12v.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="9" y1="16.5" x2="9" y2="19.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="12" y1="16.5" x2="12" y2="21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="15" y1="16.5" x2="15" y2="19.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </SvgIcon>
);

const RefundsIssuedIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path
            d="M8 8H4v-4M4 8a8.5 8.5 0 0 1 15 1"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M16 16h4v4M20 16a8.5 8.5 0 0 1-15-1"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </SvgIcon>
);

export default function LiveCashFlowTab() {
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const activeTenant = tenants.find(t =>
        String(t.tenantId || t.id || t._id) === String(tenantId) ||
        (authData.hotelId && String(t.id || t._id) === String(authData.hotelId))
    );

    const { currency: contextCurrency } = useCurrency();
    const isUae = isUaeProperty(activeTenant) || activeTenant?.currency === 'AED' || authData?.currency === 'AED' || contextCurrency === 'AED';
    const currency = isUae ? 'AED' : (contextCurrency || activeTenant?.currency || 'INR');

    // Live Cash Flow state
    const todayStr = new Date().toISOString().split('T')[0];
    const selectedDate = todayStr;
    const selectedShiftId = 'all';
    const [loading, setLoading] = useState(false);
    const [shifts, setShifts] = useState([]);
    const [cashDropModalOpen, setCashDropModalOpen] = useState(false);

    // Live Cash Flow data state (populated directly via GET /api/v1/finance/shifts/:id/liquidity-summary)
    const [cashFlowData, setCashFlowData] = useState({
        businessDate: todayStr,
        totalNetCashOnHand: 0,
        expectedPhysicalCash: 0,
        netOperationalCollections: 0,
        outlet: '',
        openingFloat: 0,
        liquidityStatus: 'Balanced',
        totalInflow: 0,
        totalOutflow: 0,
        inflow: {
            total: 0,
            categories: [
                {
                    id: 'advance_deposits',
                    title: 'Advance Deposits',
                    subtitle: 'Prepayments for future reservations',
                    description: 'Tracks prepayments collected today for future stays. Accounts for money entering the business today for services that will be rendered later.',
                    amount: 0,
                    count: 0,
                    type: 'advance'
                },
                {
                    id: 'folio_settlements',
                    title: 'Folio Settlements',
                    subtitle: 'Payments collected at front desk / checkout',
                    description: 'Captures payments made at the front desk by checking-out or in-house guests to clear their active room balances.',
                    amount: 0,
                    count: 0,
                    type: 'folio'
                },
                {
                    id: 'pos_cash',
                    title: 'Point of Sale (POS) Cash',
                    subtitle: 'F&B and direct amenity receipts',
                    description: 'Direct receipts from restaurant POS and amenity outlets.',
                    amount: 0,
                    count: 0,
                    type: 'pos'
                }
            ]
        },
        outflow: {
            total: 0,
            categories: [
                {
                    id: 'refunds_issued',
                    title: 'Refunds Issued',
                    subtitle: 'Cancellations or folio overpayments',
                    description: 'Logs money returned directly to guests due to booking cancellations, overpayments, or service disputes.',
                    amount: 0,
                    count: 0,
                    type: 'refund'
                },
                {
                    id: 'petty_cash',
                    title: 'Operational Expenses (Petty Cash)',
                    subtitle: 'Minor daily purchases paid from till',
                    description: 'Tracks minor, ad-hoc purchases made directly from the front desk till, such as emergency maintenance supplies or emergency F&B groceries.',
                    amount: 0,
                    count: 0,
                    type: 'petty_cash'
                },
                {
                    id: 'vendor_payouts',
                    title: 'Vendor Payouts',
                    subtitle: 'Cash given to suppliers / contractors',
                    description: 'Records cash handed to external suppliers, delivery drivers, or local contractors directly from the day’s physical collections.',
                    amount: 0,
                    count: 0,
                    type: 'vendor'
                },
                {
                    id: 'bank_deposits',
                    title: 'Bank Deposits (Cash Drops)',
                    subtitle: 'Cash transferred to safe or bank',
                    description: 'Tracks money physically removed from the active drawer to be secured in a safe or deposited at the bank. While not a business loss, it reduces the active till balance and must be tracked to close the cashier shift accurately.',
                    amount: 0,
                    count: 0,
                    type: 'bank_deposit',
                    highlighted: true
                }
            ]
        }
    });

    // Helper to safely extract cashier shifts list from various API response shapes
    const extractShiftsList = (res) => {
        if (!res) return [];
        if (Array.isArray(res)) return res;
        if (Array.isArray(res.shifts)) return res.shifts;
        if (Array.isArray(res.data)) return res.data;
        if (res.shift && typeof res.shift === 'object') return [res.shift];
        if (res.data?.shifts && Array.isArray(res.data.shifts)) return res.data.shifts;
        if (res.data?.shift && typeof res.data.shift === 'object') return [res.data.shift];
        if (res.id) return [res];
        if (res.data?.id) return [res.data];
        return [];
    };

    // Load open cashier shifts for the shift filter
    useEffect(() => {
        const loadActiveShifts = async () => {
            try {
                const res = await FinanceService.getOpenCashierShifts(tenantId);
                const list = extractShiftsList(res);
                setShifts(list);
            } catch (err) {
                console.warn('Could not load cashier shifts in LiveCashFlowTab:', err);
            }
        };
        loadActiveShifts();
    }, [tenantId]);

    const fetchLiveCashFlow = async () => {
        setLoading(true);
        try {
            // 1. Ensure current active shifts are available
            let currentShifts = shifts;
            if (!currentShifts || currentShifts.length === 0) {
                try {
                    const res = await FinanceService.getOpenCashierShifts(tenantId);
                    currentShifts = extractShiftsList(res);
                    setShifts(currentShifts);
                } catch (_) {}
            }

            // 2. Fetch Shift Liquidity Summary (GET /api/v1/finance/shifts/:id/liquidity-summary)
            let summaries = [];

            if (selectedShiftId !== 'all') {
                try {
                    const shiftData = await FinanceService.getShiftLiquiditySummary(selectedShiftId, tenantId);
                    if (shiftData) {
                        summaries.push(shiftData);
                    }
                } catch (err) {
                    console.warn(`Could not load liquidity summary for shift ${selectedShiftId}:`, err);
                }
            } else if (currentShifts && currentShifts.length > 0) {
                const results = await Promise.allSettled(
                    currentShifts.map(s => FinanceService.getShiftLiquiditySummary(s.id, tenantId))
                );
                results.forEach(r => {
                    if (r.status === 'fulfilled' && r.value) {
                        summaries.push(r.value);
                    }
                });
            }

            // 3. Aggregate shift data if summaries exist
            if (summaries.length > 0) {
                let totalAdvanceDeposits = 0;
                let totalAdvanceCount = 0;
                let totalFolioSettlements = 0;
                let totalFolioCount = 0;
                let totalPosCash = 0;
                let totalPosCount = 0;

                let totalRefunds = 0;
                let totalRefundsCount = 0;
                let totalPettyCash = 0;
                let totalPettyCashCount = 0;
                let totalVendorPayouts = 0;
                let totalVendorPayoutsCount = 0;
                let totalCashDrops = 0;
                let totalCashDropsCount = 0;

                let aggregatedOpeningFloat = 0;
                let aggregatedExpectedCash = 0;
                let latestReconciliationStatus = 'Balanced';
                let totalNetOperationalCollections = 0;
                let outletName = '';

                summaries.forEach(s => {
                    // Support both nested s.inflows/s.outflows and top-level fields
                    const adv = Number(s.inflows?.advanceDeposits ?? s.advanceDeposits ?? 0);
                    const fol = Number(s.inflows?.folioSettlements ?? s.folioSettlements ?? 0);
                    const pos = Number(s.inflows?.posCash ?? s.inflows?.pointOfSaleCash ?? s.posCash ?? 0);

                    const ref = Number(s.outflows?.refunds ?? s.refunds ?? 0);
                    const petty = Number(s.outflows?.pettyCash ?? s.pettyCash ?? 0) + Number(s.outflows?.otherExpenses ?? s.otherExpenses ?? 0);
                    const vendor = Number(s.outflows?.vendorPayouts ?? s.vendorPayouts ?? 0);
                    const drops = Number(s.outflows?.cashDrops ?? s.cashDrops ?? 0);

                    totalAdvanceDeposits += adv;
                    totalAdvanceCount += (s.advanceDepositsCount || (adv > 0 ? 1 : 0));
                    totalFolioSettlements += fol;
                    totalFolioCount += (s.folioSettlementsCount || (fol > 0 ? 1 : 0));
                    totalPosCash += pos;
                    totalPosCount += (s.posCashCount || (pos > 0 ? 1 : 0));

                    totalRefunds += ref;
                    totalRefundsCount += (s.refundsCount || (ref > 0 ? 1 : 0));
                    totalPettyCash += petty;
                    totalPettyCashCount += (s.pettyCashCount || (petty > 0 ? 1 : 0));
                    totalVendorPayouts += vendor;
                    totalVendorPayoutsCount += (s.vendorPayoutsCount || (vendor > 0 ? 1 : 0));
                    totalCashDrops += drops;
                    totalCashDropsCount += (s.cashDropsCount || s.cashDropsList?.length || (drops > 0 ? 1 : 0));

                    const floatVal = Number(s.openingFloat ?? s.shift?.openingFloat ?? 0);
                    aggregatedOpeningFloat += floatVal;
                    aggregatedExpectedCash += Number(
                        s.expectedPhysicalCash ??
                        (floatVal + Number(s.inflows?.byTender?.Cash || adv + fol + pos) - Number(s.outflows?.byTender?.Cash || ref + petty + vendor) - drops)
                    );

                    // Net Operational Collections from API response
                    if (s.netOperationalCollections !== undefined && s.netOperationalCollections !== null) {
                        totalNetOperationalCollections += Number(s.netOperationalCollections);
                    } else {
                        const sIn = Number(s.inflows?.totalInflows ?? (adv + fol + pos));
                        const sOut = Number(s.outflows?.operationalOutflows ?? (ref + petty + vendor));
                        totalNetOperationalCollections += (sIn - sOut);
                    }

                    if (s.shift?.outlet && !outletName) {
                        outletName = s.shift.outlet;
                    }

                    if (s.reconciliationStatus) {
                        latestReconciliationStatus = s.reconciliationStatus;
                    }
                });

                if (!outletName && currentShifts && currentShifts.length > 0) {
                    outletName = currentShifts[0]?.outlet || '';
                }

                // Compute totalIn, totalOut, and netCash
                const totalIn = totalAdvanceDeposits + totalFolioSettlements + totalPosCash;
                const totalOut = totalRefunds + totalPettyCash + totalVendorPayouts + totalCashDrops;
                const netCash = aggregatedExpectedCash;
                const netOperational = totalNetOperationalCollections;

                setCashFlowData({
                    businessDate: selectedDate,
                    totalNetCashOnHand: netCash,
                    expectedPhysicalCash: aggregatedExpectedCash,
                    netOperationalCollections: netOperational,
                    outlet: outletName,
                    openingFloat: aggregatedOpeningFloat,
                    liquidityStatus: latestReconciliationStatus || (netCash > 0 ? 'Positive Liquidity' : (netCash < 0 ? 'Negative Liquidity' : 'Balanced')),
                    totalInflow: totalIn,
                    totalOutflow: totalOut,
                    inflow: {
                        total: totalIn,
                        categories: [
                            {
                                id: 'advance_deposits',
                                title: 'Advance Deposits',
                                subtitle: 'Prepayments for future reservations',
                                description: 'Tracks prepayments collected today for future stays.',
                                amount: totalAdvanceDeposits,
                                count: totalAdvanceCount,
                                type: 'advance'
                            },
                            {
                                id: 'folio_settlements',
                                title: 'Folio Settlements',
                                subtitle: 'Payments collected at front desk / checkout',
                                description: 'Captures payments made at front desk by checking-out or in-house guests.',
                                amount: totalFolioSettlements,
                                count: totalFolioCount,
                                type: 'folio'
                            },
                            {
                                id: 'pos_cash',
                                title: 'Point of Sale (POS) Cash',
                                subtitle: 'F&B and direct amenity receipts',
                                description: 'Direct receipts from restaurant POS and amenity outlets.',
                                amount: totalPosCash,
                                count: totalPosCount,
                                type: 'pos'
                            }
                        ]
                    },
                    outflow: {
                        total: totalOut,
                        categories: [
                            {
                                id: 'refunds_issued',
                                title: 'Refunds Issued',
                                subtitle: 'Cancellations or folio overpayments',
                                description: 'Logs money returned directly to guests due to booking cancellations, overpayments, or service disputes.',
                                amount: totalRefunds,
                                count: totalRefundsCount,
                                type: 'refund'
                            },
                            {
                                id: 'petty_cash',
                                title: 'Operational Expenses (Petty Cash)',
                                subtitle: 'Minor daily purchases paid from till',
                                description: 'Tracks minor, ad-hoc purchases made directly from the front desk till.',
                                amount: totalPettyCash,
                                count: totalPettyCashCount,
                                type: 'petty_cash'
                            },
                            {
                                id: 'vendor_payouts',
                                title: 'Vendor Payouts',
                                subtitle: 'Cash given to suppliers / contractors',
                                description: 'Records cash handed to external suppliers, delivery drivers, or local contractors.',
                                amount: totalVendorPayouts,
                                count: totalVendorPayoutsCount,
                                type: 'vendor'
                            },
                            {
                                id: 'bank_deposits',
                                title: 'Bank Deposits (Cash Drops)',
                                subtitle: 'Cash transferred to safe or bank',
                                description: 'Tracks money physically removed from the active drawer to be secured in a safe.',
                                amount: totalCashDrops,
                                count: totalCashDropsCount,
                                type: 'bank_deposit',
                                highlighted: true
                            }
                        ]
                    }
                });
            } else {
                // No open cashier shift recorded for this date
                const fallbackFloat = Number(currentShifts?.[0]?.openingFloat || 0);
                setCashFlowData({
                    businessDate: selectedDate,
                    totalNetCashOnHand: fallbackFloat,
                    expectedPhysicalCash: fallbackFloat,
                    netOperationalCollections: 0,
                    outlet: currentShifts?.[0]?.outlet || '',
                    openingFloat: fallbackFloat,
                    liquidityStatus: 'Balanced',
                    totalInflow: 0,
                    totalOutflow: 0,
                    inflow: {
                        total: 0,
                        categories: [
                            {
                                id: 'advance_deposits',
                                title: 'Advance Deposits',
                                subtitle: 'Prepayments for future reservations',
                                description: 'Tracks prepayments collected today for future stays.',
                                amount: 0,
                                count: 0,
                                type: 'advance'
                            },
                            {
                                id: 'folio_settlements',
                                title: 'Folio Settlements',
                                subtitle: 'Payments collected at front desk / checkout',
                                description: 'Captures payments made at front desk by checking-out or in-house guests.',
                                amount: 0,
                                count: 0,
                                type: 'folio'
                            },
                            {
                                id: 'pos_cash',
                                title: 'Point of Sale (POS) Cash',
                                subtitle: 'F&B and direct amenity receipts',
                                description: 'Direct receipts from restaurant POS and amenity outlets.',
                                amount: 0,
                                count: 0,
                                type: 'pos'
                            }
                        ]
                    },
                    outflow: {
                        total: 0,
                        categories: [
                            {
                                id: 'refunds_issued',
                                title: 'Refunds Issued',
                                subtitle: 'Cancellations or folio overpayments',
                                description: 'Logs money returned directly to guests.',
                                amount: 0,
                                count: 0,
                                type: 'refund'
                            },
                            {
                                id: 'petty_cash',
                                title: 'Operational Expenses (Petty Cash)',
                                subtitle: 'Minor daily purchases paid from till',
                                description: 'Tracks minor, ad-hoc purchases made directly from the front desk till.',
                                amount: 0,
                                count: 0,
                                type: 'petty_cash'
                            },
                            {
                                id: 'vendor_payouts',
                                title: 'Vendor Payouts',
                                subtitle: 'Cash given to suppliers / contractors',
                                description: 'Records cash handed to external suppliers.',
                                amount: 0,
                                count: 0,
                                type: 'vendor'
                            },
                            {
                                id: 'bank_deposits',
                                title: 'Bank Deposits (Cash Drops)',
                                subtitle: 'Cash transferred to safe or bank',
                                description: 'Tracks money physically removed from the active drawer to be secured in a safe.',
                                amount: 0,
                                count: 0,
                                type: 'bank_deposit',
                                highlighted: true
                            }
                        ]
                    }
                });
            }
        } catch (error) {
            console.error('Failed to load shift liquidity summary:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLiveCashFlow();
    }, [tenantId]);

    // Format date string for display (e.g. "Oct 23, 2026")
    const formatDisplayDate = (dStr) => {
        if (!dStr) return '';
        const d = new Date(dStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Format currency amount helper
    const formatAmt = (val) => {
        return formatCurrency(val, currency);
    };

    const renderInflowIcon = (type) => {
        switch (type) {
            case 'advance':
                return <BookmarkBorderIcon sx={{ color: '#4f46e5', fontSize: 20 }} />;
            case 'folio':
                return <DescriptionOutlinedIcon sx={{ color: '#4f46e5', fontSize: 20 }} />;
            case 'pos':
            default:
                return <ShoppingBagOutlinedIcon sx={{ color: '#4f46e5', fontSize: 20 }} />;
        }
    };

    const renderOutflowIcon = (type) => {
        switch (type) {
            case 'refund':
                return <RefundsIssuedIcon sx={{ color: '#10b981', fontSize: 20 }} />;
            case 'petty_cash':
                return <CreditCardOutlinedIcon sx={{ color: '#10b981', fontSize: 20 }} />;
            case 'vendor':
                return <LocalShippingOutlinedIcon sx={{ color: '#10b981', fontSize: 20 }} />;
            case 'bank_deposit':
            default:
                return <AccountBalanceOutlinedIcon sx={{ color: '#10b981', fontSize: 20 }} />;
        }
    };

    return (
        <Box sx={{ width: '100%', pb: 3 }}>
            {/* ─── Top Header & Subheader Bar ───────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: 3
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, fontSize: '1.25rem' }}>
                        Live Cash Flow
                    </Typography>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: '#f2f7fe',
                            border: '1px solid #d6e9fd',
                            borderRadius: '8px',
                            py: 0.75,
                            px: 1.5
                        }}
                    >
                        <InfoOutlinedIcon sx={{ color: '#64748b', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '13px', fontWeight: 500 }}>
                            Real-time room occupancy, revenue flow, and front desk operations.
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant="contained"
                    onClick={() => setCashDropModalOpen(true)}
                    startIcon={<DropCashIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13.5px',
                        bgcolor: '#4146e0',
                        '&:hover': { bgcolor: '#3539bc' },
                        py: 1,
                        px: 2.5,
                        boxShadow: 'none',
                        height: 40
                    }}
                >
                    Drop Cash to Safe
                </Button>
            </Box>

            {/* ─── Hero Summary Cards ─────────────────────────────────────── */}
            <Grid container spacing={3} sx={{ mb: 3 }} alignItems="stretch">
                {/* Left Card: Today's Collection */}
                <Grid item xs={12} md={4.5} lg={4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: '16px',
                            border: '1px solid #eaecf0',
                            bgcolor: '#ffffff',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '10px',
                                    bgcolor: '#f4f3ff',
                                    border: '1px solid #e0e7ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#4f46e5'
                                }}
                            >
                                <MoneyBagIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#475569',
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        fontSize: '11px',
                                        display: 'block',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    TODAY'S COLLECTION
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '12px' }}>
                                    Net Operational
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: '#0f172a',
                                    fontWeight: 800,
                                    fontSize: { xs: '26px', sm: '32px' },
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1.1
                                }}
                            >
                                {formatAmt(cashFlowData.netOperationalCollections)}
                            </Typography>
                            <Chip
                                label={cashFlowData.liquidityStatus || 'Balanced'}
                                size="small"
                                sx={{
                                    bgcolor: '#ecfdf3',
                                    color: '#067647',
                                    border: '1px solid #abefc6',
                                    borderRadius: '16px',
                                    fontWeight: 600,
                                    fontSize: '11px',
                                    height: 22,
                                    px: 0.5
                                }}
                            />
                        </Box>

                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '12px', mt: 1 }}>
                            Active Shift / Today ({formatDisplayDate(selectedDate)})
                        </Typography>
                    </Paper>
                </Grid>

                {/* Right Card: Metrics Breakdown */}
                <Grid item xs={12} md={7.5} lg={8}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: '16px',
                            border: '1px solid #eaecf0',
                            bgcolor: '#ffffff',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Grid container spacing={0} sx={{ height: '100%', width: '100%' }}>
                            {/* Column 1: Opening Float */}
                            <Grid item xs={6} sm={3} sx={{ pr: { xs: 1, sm: 2.5 }, borderRight: '1px dashed #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <OpeningFloatIcon sx={{ fontSize: 22, color: '#94a3b8', mb: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                                        Opening Float
                                    </Typography>
                                    <Tooltip title="Initial cash float in till when the shift was opened" arrow placement="top">
                                        <InfoOutlinedIcon sx={{ fontSize: 13, color: '#94a3b8', cursor: 'help' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '18px' }}>
                                    {formatAmt(cashFlowData.openingFloat)}
                                </Typography>
                            </Grid>

                            {/* Column 2: Cash In Drawer */}
                            <Grid item xs={6} sm={3} sx={{ px: { xs: 1, sm: 2.5 }, borderRight: { sm: '1px dashed #e2e8f0' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <CashDrawerIcon sx={{ fontSize: 22, color: '#94a3b8', mb: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                                        Cash In Drawer
                                    </Typography>
                                    <Tooltip title="Expected physical cash in till = Opening Float + Cash Inflows - Cash Outflows" arrow placement="top">
                                        <InfoOutlinedIcon sx={{ fontSize: 13, color: '#94a3b8', cursor: 'help' }} />
                                    </Tooltip>
                                </Box>
                                <Typography variant="h6" sx={{ color: '#00a8e8', fontWeight: 700, fontSize: '18px' }}>
                                    {formatAmt(cashFlowData.totalNetCashOnHand)}
                                </Typography>
                            </Grid>

                            {/* Column 3: Total Inflow */}
                            <Grid item xs={6} sm={3} sx={{ px: { xs: 1, sm: 2.5 }, borderRight: '1px dashed #e2e8f0', mt: { xs: 2, sm: 0 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <TotalInflowIcon sx={{ fontSize: 22, color: '#94a3b8', mb: 1 }} />
                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '12px', fontWeight: 500, mb: 0.5 }}>
                                    Total Inflow
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#00c853', fontWeight: 700, fontSize: '18px' }}>
                                    + {formatAmt(cashFlowData.totalInflow)}
                                </Typography>
                            </Grid>

                            {/* Column 4: Total Outflow */}
                            <Grid item xs={6} sm={3} sx={{ pl: { xs: 1, sm: 2.5 }, mt: { xs: 2, sm: 0 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <TotalOutflowIcon sx={{ fontSize: 22, color: '#94a3b8', mb: 1 }} />
                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '12px', fontWeight: 500, mb: 0.5 }}>
                                    Total Outflow
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#f43f5e', fontWeight: 700, fontSize: '18px' }}>
                                    - {formatAmt(cashFlowData.totalOutflow)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {/* ─── Two-Column Breakdown (Cash Inflow & Cash Outflow) ──────────── */}
            <Grid container spacing={3}>
                {/* ─── Column 1: Cash Inflow ───────────────────────────────── */}
                <Grid item xs={12} md={6}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '16px',
                            border: '1px solid #eaecf0',
                            overflow: 'hidden',
                            bgcolor: '#ffffff'
                        }}
                    >
                        {/* Column Header */}
                        <Box
                            sx={{
                                bgcolor: '#ffffff',
                                p: '20px 24px',
                                borderBottom: '1px solid #eaecf0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        bgcolor: '#ffffff',
                                        border: '1px solid #abefc6',
                                        color: '#10b981',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <ArrowDownwardIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                        Cash Inflow
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '10.5px',
                                            letterSpacing: '0.06em',
                                            color: '#64748b',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        MONEY RECEIVED TODAY
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '20px' }}>
                                {formatAmt(cashFlowData.inflow?.total || cashFlowData.totalInflow)}
                            </Typography>
                        </Box>

                        {/* List Items */}
                        <Box sx={{ p: '20px 24px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {cashFlowData.inflow?.categories?.map((cat) => (
                                <Box
                                    key={cat.id}
                                    sx={{
                                        p: '16px 20px',
                                        borderRadius: '12px',
                                        border: '1px solid #eaecf0',
                                        bgcolor: '#ffffff',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            borderColor: '#cbd5e1',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                                        <Box
                                            sx={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: '10px',
                                                bgcolor: '#eff4ff',
                                                border: '1px solid #dbe4ff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            {renderInflowIcon(cat.type)}
                                        </Box>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '13.5px' }}>
                                                    {cat.title}
                                                </Typography>
                                                {cat.description && (
                                                    <Tooltip title={cat.description} arrow placement="top">
                                                        <InfoOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help', '&:hover': { color: '#0284c7' } }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '12px' }}>
                                                {cat.subtitle}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '14.5px' }}>
                                            {formatAmt(cat.amount)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '11px', display: 'block' }}>
                                            {cat.count} {cat.count === 1 ? 'transaction' : 'transactions'}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                </Grid>

                {/* ─── Column 2: Cash Outflow ──────────────────────────────── */}
                <Grid item xs={12} md={6}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '16px',
                            border: '1px solid #eaecf0',
                            overflow: 'hidden',
                            bgcolor: '#ffffff'
                        }}
                    >
                        {/* Column Header */}
                        <Box
                            sx={{
                                bgcolor: '#fff1f2',
                                p: '20px 24px',
                                borderBottom: '1px solid #ffe4e6',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        bgcolor: '#ffffff',
                                        border: '1px solid #fecdd3',
                                        color: '#f43f5e',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                        Cash Outflow
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '10.5px',
                                            letterSpacing: '0.06em',
                                            color: '#64748b',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        MONEY LEAVING TILL TODAY
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '20px' }}>
                                {formatAmt(cashFlowData.outflow?.total || cashFlowData.totalOutflow)}
                            </Typography>
                        </Box>

                        {/* List Items */}
                        <Box sx={{ p: '20px 24px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {cashFlowData.outflow?.categories?.map((cat) => {
                                const isHighlighted = cat.highlighted || cat.id === 'bank_deposits' || cat.type === 'bank_deposit';
                                return (
                                    <Box
                                        key={cat.id}
                                        sx={{
                                            p: '16px 20px',
                                            borderRadius: '12px',
                                            border: '1px solid #eaecf0',
                                            bgcolor: '#ffffff',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                borderColor: '#cbd5e1',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                                            <Box
                                                sx={{
                                                    width: 38,
                                                    height: 38,
                                                    borderRadius: '10px',
                                                    bgcolor: '#ecfdf5',
                                                    border: '1px solid #d1fae5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {renderOutflowIcon(cat.type)}
                                            </Box>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: isHighlighted ? '#4338ca' : '#1e293b',
                                                            fontSize: '13.5px'
                                                        }}
                                                    >
                                                        {cat.title}
                                                    </Typography>
                                                    {cat.description && (
                                                        <Tooltip title={cat.description} arrow placement="top">
                                                            <InfoOutlinedIcon
                                                                sx={{
                                                                    fontSize: 14,
                                                                    color: isHighlighted ? '#818cf8' : '#94a3b8',
                                                                    cursor: 'help',
                                                                    '&:hover': { color: '#0284c7' }
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: isHighlighted ? '#6366f1' : '#64748b',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {cat.subtitle}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: isHighlighted ? '#4338ca' : '#0f172a',
                                                    fontSize: '14.5px'
                                                }}
                                            >
                                                {formatAmt(cat.amount)}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: isHighlighted ? '#6366f1' : '#64748b',
                                                    fontSize: '11px',
                                                    display: 'block'
                                                }}
                                            >
                                                {cat.count} {cat.count === 1 ? 'transaction' : 'transactions'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>



            {/* Cash Drop to Safe Modal */}
            <RecordCashDropModal
                open={cashDropModalOpen}
                onClose={() => setCashDropModalOpen(false)}
                shift={shifts.find(s => s.id === selectedShiftId) || (shifts.length > 0 ? shifts[0] : null)}
                onSuccess={() => fetchLiveCashFlow()}
                tenantId={tenantId}
            />
        </Box>
    );
}
